/** Request para geração de PDF Jasper de um médico (endpoint aceita um médico por chamada). */
export interface RelatorioSelecaoExportRequest {
    nuCrm: number;
    sgUf: string;
    mostraNome: boolean;
    mostraCrm: boolean;
    mostraCpf: boolean;
    mostraDtNascimento: boolean;
    mostraSituacao: boolean;
    mostraTipoInscricao: boolean;
    mostraEmail: boolean;
    mostraEspecialidade: boolean;
    mostraEndereco: boolean;
    mostraFormacao: boolean;
    emissor: string;
}

/** Referência de um médico dentro de um lote (CRM + UF). */
export interface MedicoRefRequest {
    nuCrm: number;
    sgUf: string;
}

/** Request para geração de um único PDF combinando N médicos (formato tabular do legado). */
export interface RelatorioSelecaoLoteExportRequest {
    medicos: MedicoRefRequest[];
    mostraNome: boolean;
    mostraCrm: boolean;
    mostraCpf: boolean;
    mostraDtNascimento: boolean;
    mostraSituacao: boolean;
    mostraTipoInscricao: boolean;
    mostraEmail: boolean;
    mostraEspecialidade: boolean;
    mostraEndereco: boolean;
    mostraFormacao: boolean;
    /** Ids dos campos de identificação na ordem escolhida em "Campos" (ex.: ['nome','crm','cpf']). */
    ordemCampos: string[];
    emissor: string;
}

/** Resposta do endpoint de geração síncrona (HTTP 200): PDF em base64. */
export interface RelatorioSelecaoExportResponse {
    pdf?: string | null;
    message?: string | null;
    file_name?: string | null;
}

/** Resposta do endpoint de geração assíncrona (HTTP 202): jobId para polling. */
export interface RelatorioSelecaoJobCreatedResponse {
    jobId: string;
}

export interface MedicoListItem {
    nuCrm: number;
    nuCrmFormatado: string;
    sgUf: string;
    nmSolicitante: string;
    dsSituacao: string;
    nuCpfCgc: string;
    dsEspecialidadePrincipal: string;
}

/** Resultado da pesquisa — `truncado=true` quando o limite de 200 registros foi atingido. */
export interface RelatorioSelecaoPesquisaResponse {
    itens: MedicoListItem[];
    truncado: boolean;
}

// ── Enums de domínio ──────────────────────────────────────────────────────────

/** Status de atividade do médico no cadastro (IN_ATIVO legado: N=Ativo, S=Inativo). */
export enum EStatusAtivo {
    TODOS = '',
    ATIVO = 'N',
    INATIVO = 'S'
}

/** Tipos de inscrição disponíveis no CFM. */
export enum ETipoInscricao {
    TODAS = '',
    PRINCIPAL = 'P',
    SECUNDARIA = 'S',
    PROVISORIA = 'V',
    PROV_SECUNDARIA = 'R',
    ESTUDANTE_EXTERIOR = 'E',
    INTERCAMBISTA = 'I',
    TRANSFERENCIA = 'T'
}

/** Categoria do médico conforme vínculo militar. */
export enum ECategoriaMilitar {
    TODOS = '',
    CIVIL = 'C',
    MILITAR = 'M',
    CONVOCADO = 'V'
}

/** Sexo biológico para filtro de cadastro. */
export enum ESexo {
    TODOS = '',
    MASCULINO = 'M',
    FEMININO = 'F'
}

/** Indica se o médico apresentou diploma de graduação. */
export enum EApresentouDiploma {
    TODOS = '',
    SIM = 'S',
    NAO = 'N'
}

/** Tipo de perito: todos / apenas peritos / apenas não peritos. */
export enum ETipoPerito {
    TODOS = '',
    PERITO = 'S',
    NAO_PERITO = 'N'
}

/** Tipo de abrangência do filtro geográfico: município ou delegacia. */
export enum ETipoLocalAbrangencia {
    MUNICIPIO = 'municipio',
    DELEGACIA = 'delegacia'
}

// ── SearchFilters completo (espelha as abas do modal de seleção) ──────────────

/** Conjunto completo de filtros das abas da tela de seleção de médicos. */
export interface SearchFilters {
    // Aba 1 — Médico
    nmSolicitante: string;
    nuCrm: string;
    sgUf: string;
    nuCpfCgc: string;
    nuRg: string;
    inAtivo: string; // EStatusAtivo
    dsSituacao: string;
    inCategoria: string; // ECategoriaMilitar
    sgSexo: string; // ESexo
    dtNascimentoInicio: Date | null;
    dtNascimentoFim: Date | null;
    inJubilado: boolean;
    inDeficiente: boolean;

    // Aba 2 — Tipo de Inscrição
    inTipoInscricao: string; // ETipoInscricao
    dtInscricaoInicio: Date | null;
    dtInscricaoFim: Date | null;

    // Aba 3 — Histórico de Inscrição
    dtHistoricoInicio: Date | null;
    dtHistoricoFim: Date | null;
    sgUfTransferencia: string;

    // Aba 4 — Formação
    /** Id da escola/faculdade escolhida via `app-pesquisar-escola-dialog` (ver `EscolaRow.id`). */
    idEscolaFormacao: number | null;
    /** UF da escola escolhida — junto com `idEscolaFormacao` forma a chave composta usada pelo backend. */
    sgUfEscolaFormacao: string;
    sgUfFormacao: string;
    dtFormaturaInicio: Date | null;
    dtFormaturaFim: Date | null;
    inApresentouDiploma: string; // EApresentouDiploma

    // Aba 5 — Especialidade
    idEspecialidade: string;
    idAreaAtuacao: string;
    inPerito: string; // ETipoPerito

    // Aba 6 — Local
    tipoLocalAbrangencia: string; // ETipoLocalAbrangencia
    nmMunicipio: string;
    nmDelegacia: string;
}
